import { triggerDocumentExtraction } from "@/api/documents";
import {
    isExtractionTriggerConflict,
    isExtractionUnavailableDetail,
    isExtractionUnavailableResponse,
    type FieldExtractionStatus,
} from "@/api/documents/documents.types";
import { IModalAction, Modal } from "@/components/shared/Modal";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDownloadDocumentExportMutation } from "@/hooks/useDownloadDocumentExportMutation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
    FileSpreadsheet,
    Loader2,
    MoreVertical,
    RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
    formatExtractionConflictToast,
    formatExtractionToast,
    formatExtractionUnavailableToast,
} from "./extraction-toast-helpers";

interface AdminExtractionMenuProps {
    documentId: number;
    fieldExtractionStatus: FieldExtractionStatus;
    fieldExtractionAvailable: boolean;
    formExtractionAvailable: boolean;
}

/**
 * Admin kebab menu on "Extracted fields"; the `user.is_admin` gate is
 * enforced upstream by `DocumentDetailPanel`.
 */
export function AdminExtractionMenu({
    documentId,
    fieldExtractionStatus,
    fieldExtractionAvailable,
    formExtractionAvailable,
}: AdminExtractionMenuProps) {
    const queryClient = useQueryClient();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const anyPipelineAvailable =
        fieldExtractionAvailable || formExtractionAvailable;

    const documentExportMutation = useDownloadDocumentExportMutation();

    const mutation = useMutation({
        mutationFn: () => triggerDocumentExtraction(documentId),
        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-extracted-fields", documentId],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-coi", documentId],
            });
            toast.info(formatExtractionToast(response));
        },
        onError: (err) => {
            if (axios.isAxiosError(err)) {
                const status = err.response?.status;
                const data = err.response?.data;
                if (status === 409 && isExtractionTriggerConflict(data)) {
                    toast.info(formatExtractionConflictToast(data));
                    return;
                }
                if (status === 400 && isExtractionUnavailableResponse(data)) {
                    toast.warning(formatExtractionUnavailableToast(data));
                    return;
                }
                if (status === 400 && isExtractionUnavailableDetail(data)) {
                    toast.warning(data.detail);
                    return;
                }
                if (status === 404) {
                    toast.error("Document not found");
                    return;
                }
            }
            toast.error(
                "Could not start extraction. Please try again or contact support."
            );
        },
        onSettled: () => setConfirmOpen(false),
    });

    const confirmActions: IModalAction[] = [
        {
            label: "Re-run extraction",
            onClick: () => mutation.mutate(),
            color: "error",
            disabled: mutation.isPending,
        },
    ];

    return (
        <>
            <DropdownMenu>
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <DropdownMenuTrigger
                                aria-label="Admin actions"
                                className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/40 inline-flex size-6 cursor-pointer items-center justify-center rounded-md outline-none focus-visible:ring-2"
                            />
                        }
                    >
                        <MoreVertical className="size-3.5" strokeWidth={2.2} />
                    </TooltipTrigger>
                    <TooltipContent side="top">Admin actions</TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" className="min-w-44">
                    <DropdownMenuGroup>
                        <DropdownMenuLabel>Admin actions</DropdownMenuLabel>
                        {anyPipelineAvailable && (
                            <DropdownMenuItem
                                variant="destructive"
                                disabled={
                                    fieldExtractionStatus === "processing"
                                }
                                onClick={() => setConfirmOpen(true)}
                            >
                                <RefreshCw />
                                Re-run extraction
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            disabled={documentExportMutation.isPending}
                            onClick={() =>
                                documentExportMutation.mutate(documentId)
                            }
                        >
                            {documentExportMutation.isPending ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                <FileSpreadsheet />
                            )}
                            Download document export
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </DropdownMenuContent>
            </DropdownMenu>

            <Modal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Re-run extraction?"
                actions={confirmActions}
                isLoading={mutation.isPending}
                alerts={[
                    {
                        severity: "warning",
                        message:
                            "Unedited field values and certificate of insurance details will be re-extracted. Manual edits to fields are kept and follow their matching rows.",
                    },
                ]}
                maxWidth="xs"
            >
                <p className="text-sm">
                    Re-run extraction on this document using the current
                    fieldset?
                </p>
            </Modal>
        </>
    );
}
